import { useEffect, useState } from "react";
import { NavLink, useNavigate, Link } from "react-router-dom";
import { LogOut, Sun, Moon, Bell, ArrowLeftRight, Settings as SettingsIcon, Building2, ShieldAlert, Search, Plus, ChevronRight, UserPlus } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useTheme } from "@/context/ThemeContext";
import { useCurrency } from "@/context/CurrencyContext";
import { useCurrencyRail } from "@/context/CurrencyRailContext";
import { useEntity, CONSOLIDATED_ID } from "@/context/EntityContext";
import { AccountrixLogo } from "@/components/AccountrixLogo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import api from "@/lib/api";
import { InstallPWA } from "@/components/InstallPWA";
import { InstalledAppTour } from "@/components/InstalledAppTour";
import { WorkspaceTypePicker } from "@/components/WorkspaceTypePicker";
import { CreateBusinessCta } from "@/components/CreateBusinessCta";
import { GROUPS, QUICK_CREATE, SEARCH_TARGETS, linkBase, linkActive, linkIdle, canSee } from "@/components/appshell/shared";

export default function AppShell({ children }) {
  const { user, logout, updateCurrency } = useAuth();
  const { theme, toggle: toggleTheme } = useTheme();
  const currencyCtx = useCurrency();
  const railCtx = useCurrencyRail();
  const entityCtx = useEntity();
  const workspaceType = entityCtx?.currentEntity?.workspace_type || null;
  const showTypePicker = !!(entityCtx?.currentEntity && !entityCtx.isConsolidated && workspaceType == null);
  const navigate = useNavigate();
  const [unread, setUnread] = useState(0);
  const [searchQ, setSearchQ] = useState("");
  const [searchOpen, setSearchOpen] = useState(false);

  const loadUnread = async () => {
    try {
      const { data } = await api.get("/notifications");
      setUnread(data.filter((n) => !n.read).length);
    } catch (err) {
      console.debug("[AppShell] load notifications:", err?.message);
    }
  };

  useEffect(() => {
    loadUnread();
    const handler = () => loadUnread();
    window.addEventListener("ledger:notifications-updated", handler);
    // Cmd/Ctrl+K opens global search
    const key = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "k") {
        e.preventDefault();
        document.querySelector('[data-testid="global-search"]')?.focus();
      }
    };
    window.addEventListener("keydown", key);
    return () => {
      window.removeEventListener("ledger:notifications-updated", handler);
      window.removeEventListener("keydown", key);
    };
  }, []);

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  const cls = ({ isActive }) => `${linkBase} ${isActive ? linkActive : linkIdle}`;

  // Filtered search results
  const q = searchQ.trim().toLowerCase();
  const searchResults = q
    ? SEARCH_TARGETS.filter((s) =>
        s.label.toLowerCase().includes(q) || s.keywords.includes(q)
      ).slice(0, 8)
    : [];

  return (
    <div className="min-h-screen bg-[var(--bg)] text-[var(--text)] flex" data-testid="app-shell">
      <InstalledAppTour />
      <WorkspaceTypePicker open={showTypePicker} entity={entityCtx?.currentEntity} />
      {/* ---------- LEFT RAIL SIDEBAR ---------- */}
      <aside
        data-testid="app-sidebar"
        className="w-60 shrink-0 border-r border-[var(--border)] bg-[var(--sidebar)] flex flex-col sticky top-0 h-screen"
      >
        {/* Brand */}
        <div className="px-5 py-4 border-b border-[var(--border)]">
          <AccountrixLogo variant="full" size="sm" testId="app-brand-mark" />
        </div>

        {/* Nav */}
        <nav className="p-3 flex-1 overflow-auto">
          {GROUPS.map((group, gi) => {
            const visible = group.items.filter((it) => canSee(user, it.perm, it.editions, workspaceType));
            if (visible.length === 0) return null;
            return (
              <div key={group.label || `nav-group-${gi}`} className={gi > 0 ? "mt-4" : ""}>
                {group.label && (
                  <div className="text-[10px] tracking-[0.18em] uppercase text-[var(--muted-text)] px-3 mb-1.5 mt-1">
                    {group.label}
                  </div>
                )}
                <div className="flex flex-col gap-0.5">
                  {visible.map((item) => {
                    const Icon = item.icon;
                    return (
                      <NavLink
                        key={item.to}
                        to={item.to}
                        end={item.end}
                        data-testid={item.testid}
                        className={cls}
                      >
                        <Icon className="w-[15px] h-[15px] shrink-0" />
                        <span className="truncate">{item.label}</span>
                      </NavLink>
                    );
                  })}
                </div>
              </div>
            );
          })}

          {/* Admin & system */}
          <div className="mt-4">
            <div className="text-[10px] tracking-[0.18em] uppercase text-[var(--muted-text)] px-3 mb-1.5">
              System
            </div>
            <div className="flex flex-col gap-0.5">
              <NavLink to="/notifications" data-testid="nav-notifications" className={cls}>
                <Bell className="w-[15px] h-[15px] shrink-0" />
                <span className="truncate flex-1">Notifications</span>
                {unread > 0 && (
                  <span data-testid="unread-badge" className="text-[10px] font-mono-data bg-[var(--expense)] text-white rounded-full px-1.5 py-px shrink-0">
                    {unread}
                  </span>
                )}
              </NavLink>
              {canSee(user, "entities") && (
                <NavLink to="/entities" data-testid="nav-entities" className={cls}>
                  <Building2 className="w-[15px] h-[15px] shrink-0" />
                  <span className="truncate">Entities</span>
                </NavLink>
              )}
              {user?.is_owner && (
                <NavLink to="/team" data-testid="nav-team" className={cls}>
                  <UserPlus className="w-[15px] h-[15px] shrink-0" />
                  <span className="truncate">Team & access</span>
                </NavLink>
              )}
              {user?.role === "admin" && (
                <NavLink to="/admin/diagnostics" data-testid="nav-diagnostics" className={cls}>
                  <ShieldAlert className="w-[15px] h-[15px] shrink-0" />
                  <span className="truncate">Diagnostics</span>
                </NavLink>
              )}
              <NavLink to="/settings" data-testid="nav-settings" className={cls}>
                <SettingsIcon className="w-[15px] h-[15px] shrink-0" />
                <span className="truncate">Settings</span>
              </NavLink>
            </div>
          </div>
        </nav>

        {/* Personal → Business upsell (renders only in Personal edition) */}
        <CreateBusinessCta />

        {/* Sidebar footer — Currency rail selector + FX toggle */}
        {currencyCtx && (
          <div className="p-3 border-t border-[var(--border)] space-y-2">
            {/* Currency rail picker — global filter, persists across pages */}
            <div className="p-2 rounded-md border border-[var(--border)]">
              <div className="text-[10px] tracking-[0.2em] uppercase text-[var(--muted-text)] mb-1.5">Currency rail</div>
              <div className="grid grid-cols-3 gap-1" data-testid="rail-picker-sidebar">
                {[
                  { key: "ALL", label: "All", color: "#2F4F4F" },
                  { key: "USD", label: "USD", color: "#1E5A96" },
                  { key: "NGN", label: "NGN", color: "#4C956C" },
                ].map((r) => {
                  const isOn = railCtx.activeRail === r.key;
                  return (
                    <button
                      key={r.key}
                      data-testid={`rail-picker-${r.key}`}
                      onClick={() => railCtx.setActiveRail(r.key)}
                      className={`text-[10px] font-medium rounded px-1.5 py-1 transition ${
                        isOn ? "text-white" : "bg-[var(--track)] text-[var(--muted-text)] hover:bg-[var(--brand-light)]"
                      }`}
                      style={isOn ? { backgroundColor: r.color } : undefined}
                    >
                      {r.label}
                    </button>
                  );
                })}
              </div>
            </div>
            {/* iter 72h — Removed global "Convert to USD" toggle. Reports
                and balances always render in the currency each transaction
                was recorded in. */}
            <div>
              <InstallPWA variant="compact" testId="sidebar-install-pwa" />
            </div>
          </div>
        )}
      </aside>

      {/* ---------- MAIN COLUMN ---------- */}
      <div className="flex-1 min-w-0 flex flex-col">
        {/* ---------- TOP BAR ---------- */}
        <header
          data-testid="app-topbar"
          className="sticky top-0 z-40 bg-[var(--topbar)] border-b border-[var(--border)] h-14 flex items-center gap-3 px-6"
        >
          {/* Entity switcher */}
          <div className="w-56 shrink-0">
            <Select
              value={entityCtx.currentEntityId || ""}
              onValueChange={(v) => entityCtx.setEntity(v)}
            >
              <SelectTrigger data-testid="entity-switcher" className="h-9 rounded-md text-sm">
                {entityCtx.currentEntity ? (
                  <span className="flex items-center gap-2 min-w-0">
                    <span
                      className="w-5 h-5 rounded-sm shrink-0 flex items-center justify-center text-xs"
                      style={{ backgroundColor: `${entityCtx.currentEntity.color}22`, color: entityCtx.currentEntity.color }}
                    >{entityCtx.currentEntity.icon}</span>
                    <span className="truncate">{entityCtx.currentEntity.name}</span>
                    {workspaceType && (
                      <span
                        data-testid="workspace-type-badge"
                        className={`shrink-0 text-[9px] uppercase tracking-widest font-medium px-1.5 py-0.5 rounded ${
                          workspaceType === "business"
                            ? "bg-[var(--brand-light)] text-[var(--brand-text)]"
                            : "bg-[#F3E9DC] text-[#8B4B18]"
                        }`}
                      >{workspaceType === "business" ? "BIZ" : "PERSONAL"}</span>
                    )}
                  </span>
                ) : (<SelectValue placeholder="Choose entity" />)}
              </SelectTrigger>
              <SelectContent>
                {entityCtx.entities.map((e) => (
                  <SelectItem key={e.id} value={e.id} data-testid={`entity-option-${e.name}`}>
                    <span className="inline-flex items-center gap-2">
                      <span style={{ color: e.color }}>{e.icon}</span>{e.name}
                    </span>
                  </SelectItem>
                ))}
                <SelectItem value={CONSOLIDATED_ID} data-testid="entity-option-consolidated">
                  <span className="inline-flex items-center gap-2">
                    <span>🌐</span>Consolidated · all
                  </span>
                </SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Global search */}
          <div className="flex-1 max-w-xl relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[var(--muted-text)] pointer-events-none" />
            <Input
              data-testid="global-search"
              placeholder="Search pages… (⌘K)"
              value={searchQ}
              onChange={(e) => { setSearchQ(e.target.value); setSearchOpen(true); }}
              onFocus={() => setSearchOpen(true)}
              onBlur={() => setTimeout(() => setSearchOpen(false), 150)}
              className="pl-9 h-9 rounded-md border-[var(--border)] bg-[var(--bg)] focus-visible:ring-2 focus-visible:ring-[var(--focus-ring)]"
            />
            {searchOpen && searchResults.length > 0 && (
              <div data-testid="search-results" className="absolute top-11 left-0 right-0 bg-[var(--surface)] border border-[var(--border)] rounded-md shadow-lg overflow-hidden">
                {searchResults.map((r) => (
                  <button
                    key={r.to}
                    onMouseDown={(e) => { e.preventDefault(); navigate(r.to); setSearchQ(""); setSearchOpen(false); }}
                    className="w-full flex items-center justify-between px-3 py-2 text-sm hover:bg-[var(--hover)] text-left"
                    data-testid={`search-result-${r.label}`}
                  >
                    <span className="truncate">{r.label}</span>
                    <ChevronRight className="w-3.5 h-3.5 text-[var(--muted-text)]" />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Quick-create "+ New" */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                data-testid="quick-create-btn"
                className="h-9 rounded-md btn-brand px-3 gap-1.5 font-medium text-sm"
              >
                <Plus className="w-4 h-4" /> New
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuLabel>Create</DropdownMenuLabel>
              <DropdownMenuSeparator />
              {QUICK_CREATE.map((qc) => (
                <DropdownMenuItem
                  key={qc.label}
                  data-testid={`quick-create-${qc.label.toLowerCase().replace(/\s+/g, '-')}`}
                  onSelect={() => navigate(qc.href)}
                  className="cursor-pointer"
                >
                  <qc.icon className="w-4 h-4 mr-2" /> {qc.label}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>

          {/* Notifications shortcut */}
          <Link
            to="/notifications"
            data-testid="topbar-notifications"
            className="relative h-9 w-9 rounded-md flex items-center justify-center hover:bg-[var(--hover)] text-[var(--muted-text)] hover:text-[var(--text)]"
            aria-label="Notifications"
          >
            <Bell className="w-4 h-4" />
            {unread > 0 && (
              <span className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 rounded-full bg-[var(--expense)] text-white text-[9px] font-mono-data flex items-center justify-center">
                {unread > 99 ? "99+" : unread}
              </span>
            )}
          </Link>

          {/* User menu */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                data-testid="user-menu-trigger"
                variant="ghost"
                className="h-9 px-2 gap-2 rounded-md"
              >
                <div className="w-7 h-7 rounded-full bg-[var(--brand)] text-white flex items-center justify-center text-xs font-semibold">
                  {(user?.name || "U").slice(0, 1).toUpperCase()}
                </div>
                <div className="hidden md:flex flex-col items-start leading-tight text-left">
                  <span className="text-xs font-medium truncate max-w-[120px]">{user?.name}</span>
                  <span className="text-[10px] text-[var(--muted-text)] truncate max-w-[120px]">{user?.email}</span>
                </div>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuLabel className="text-xs">
                <div className="truncate">{user?.email}</div>
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              <div className="px-2 py-1.5">
                <div className="text-[10px] tracking-[0.15em] uppercase text-[var(--muted-text)] mb-1">Currency</div>
                <Select value={user?.currency || "USD"} onValueChange={(v) => updateCurrency(v)}>
                  <SelectTrigger data-testid="currency-switcher" className="h-8 rounded-md text-xs">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="USD" data-testid="currency-usd">USD · US Dollar</SelectItem>
                    <SelectItem value="NGN" data-testid="currency-ngn">NGN · Nigerian Naira</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <DropdownMenuItem data-testid="theme-toggle" onSelect={toggleTheme} className="cursor-pointer">
                {theme === "dark" ? <Sun className="w-4 h-4 mr-2" /> : <Moon className="w-4 h-4 mr-2" />}
                {theme === "dark" ? "Light mode" : "Dark mode"}
              </DropdownMenuItem>
              <DropdownMenuItem onSelect={() => navigate("/settings")} data-testid="user-menu-settings" className="cursor-pointer">
                <SettingsIcon className="w-4 h-4 mr-2" /> Settings
              </DropdownMenuItem>
              <DropdownMenuItem
                data-testid="switch-workspace-menu"
                onSelect={() => {
                  // iter 72 — clear the session kind so the picker shows again.
                  // `?force=1` disables the auto-skip effect so a user who
                  // only has one kind of entity still sees the picker with a
                  // "Create the other kind" affordance instead of getting
                  // silently bounced right back.
                  try { sessionStorage.removeItem("ledger.workspace_kind"); } catch { /* ignore */ }
                  navigate("/workspace?force=1");
                }}
                className="cursor-pointer"
              >
                <ArrowLeftRight className="w-4 h-4 mr-2" /> Switch workspace
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                data-testid="logout-button"
                onSelect={handleLogout}
                className="cursor-pointer text-[var(--expense)] focus:text-[var(--expense)]"
              >
                <LogOut className="w-4 h-4 mr-2" /> Sign out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </header>

        {/* ---------- MAIN CONTENT ---------- */}
        <main className="flex-1 min-w-0">
          <div className="max-w-7xl mx-auto px-6 py-6 lg:px-8 lg:py-8">
            {children}
          </div>
        </main>
      </div>
    </div>
  );
}
